'use client';

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/**
 * SiteContent — the reusable inline-edit engine. Wrap any public page in
 * <SiteContentProvider page="artists" rich={[...]}> and use <E id default/> for
 * copy. For a visitor it renders the published copy. For an admin who opens the
 * page with ?edit=1 it becomes a live editor: click-to-edit, debounced autosave
 * into a draft, undo/redo, and a floating Publish/Discard bar. Nothing goes live
 * until Publish. The save API is the real security boundary.
 */

type Dict = Record<string, string>;

interface SiteContentCtx {
  value: (id: string, def?: string) => string;
  editing: boolean;
  rich: Set<string>;
  onEdit: (id: string, value: string) => void;
}

const Ctx = createContext<SiteContentCtx>({
  value: (_id, def) => def ?? '',
  editing: false,
  rich: new Set(),
  onEdit: () => {},
});

export const useSiteContent = () => useContext(Ctx);

interface VersionRow {
  id: string;
  published_at: string;
  published_by: string | null;
  label: string | null;
}

// Exposed to the floating toolbar via a second context so <E> stays lean.
interface BarCtx {
  editing: boolean;
  status: 'idle' | 'saving' | 'saved';
  hasDraft: boolean;
  publishing: boolean;
  canUndo: boolean;
  canRedo: boolean;
  versions: VersionRow[];
  seoFields: string[];
  value: (id: string, def?: string) => string;
  onEdit: (id: string, value: string) => void;
  publish: () => void;
  discard: () => void;
  restore: (id: string) => void;
  undo: () => void;
  redo: () => void;
  exit: () => void;
}
const Bar = createContext<BarCtx | null>(null);
export const useEditBar = () => useContext(Bar);

export function SiteContentProvider({
  page,
  rich = [],
  seoFields = [],
  children,
}: {
  page: string;
  rich?: string[];
  seoFields?: string[];
  children: React.ReactNode;
}) {
  const richSet = React.useMemo(() => new Set(rich), [rich]);
  const [published, setPublished] = useState<Dict>({});
  const [draft, setDraft] = useState<Dict | null>(null);
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [hasDraft, setHasDraft] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [versions, setVersions] = useState<VersionRow[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const draftRef = useRef<Dict>({});
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const history = useRef<{ stack: Dict[]; i: number }>({ stack: [], i: -1 });
  const syncUndo = () => {
    setCanUndo(history.current.i > 0);
    setCanRedo(history.current.i < history.current.stack.length - 1);
  };

  const loadDraft = useCallback(async () => {
    const d = await fetch(`/api/admin/content/${page}`)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
    if (!d) return;
    if (d.published) setPublished(d.published);
    const dr = (d.draft as Dict | null) || null;
    setDraft(dr);
    setHasDraft(!!dr);
    setVersions(d.versions || []);
    const base = dr || d.published || {};
    draftRef.current = { ...base };
    history.current = { stack: [{ ...base }], i: 0 };
    syncUndo();
  }, [page]);

  // Load published copy always; enter edit mode only for an admin with ?edit=1.
  useEffect(() => {
    let cancelled = false;
    fetch(`/api/content/${page}`)
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled) setPublished(d.content || {});
      })
      .catch(() => {});
    const wantEdit = new URLSearchParams(window.location.search).get('edit') === '1';
    if (wantEdit) {
      fetch('/api/am-i-admin')
        .then((r) => r.json())
        .then((d) => {
          if (!cancelled && d.admin) {
            setEditing(true);
            loadDraft();
          }
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, [page, loadDraft]);

  const save = useCallback(
    async (next: Dict) => {
      setStatus('saving');
      const changed = Object.fromEntries(Object.entries(next)); // whole draft is small
      await fetch(`/api/admin/content/${page}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(changed),
      }).catch(() => {});
      setHasDraft(true);
      setStatus('saved');
      setTimeout(() => setStatus((s) => (s === 'saved' ? 'idle' : s)), 1600);
    },
    [page]
  );

  const applyDraft = useCallback(
    (next: Dict) => {
      draftRef.current = next;
      setDraft(next);
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => save(next), 700);
      setStatus('saving');
    },
    [save]
  );

  const onEdit = useCallback(
    (id: string, val: string) => {
      const next = { ...draftRef.current, [id]: val };
      const h = history.current;
      const stack = [...h.stack.slice(0, h.i + 1), next].slice(-60);
      history.current = { stack, i: stack.length - 1 };
      syncUndo();
      applyDraft(next);
    },
    [applyDraft]
  );

  const undo = useCallback(() => {
    const h = history.current;
    if (h.i <= 0) return;
    h.i -= 1;
    syncUndo();
    applyDraft({ ...h.stack[h.i] });
  }, [applyDraft]);
  const redo = useCallback(() => {
    const h = history.current;
    if (h.i >= h.stack.length - 1) return;
    h.i += 1;
    syncUndo();
    applyDraft({ ...h.stack[h.i] });
  }, [applyDraft]);

  useEffect(() => {
    if (!editing) return;
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta || e.key.toLowerCase() !== 'z') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      e.preventDefault();
      if (e.shiftKey) redo();
      else undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing, undo, redo]);

  const publish = useCallback(async () => {
    setPublishing(true);
    try {
      const r = await fetch(`/api/admin/content/${page}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'publish' }),
      });
      const j = await r.json().catch(() => ({}));
      if (j?.content) setPublished(j.content);
      setDraft(null);
      setHasDraft(false);
      await loadDraft();
    } finally {
      setPublishing(false);
    }
  }, [page, loadDraft]);

  const discard = useCallback(async () => {
    if (!window.confirm('Discard your unpublished changes and revert to the live version?')) return;
    await fetch(`/api/admin/content/${page}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'discard' }),
    });
    await loadDraft();
    setHasDraft(false);
  }, [page, loadDraft]);

  const restore = useCallback(
    async (id: string) => {
      if (!window.confirm('Load this version into your draft to review, then Publish to go live?'))
        return;
      await fetch(`/api/admin/content/${page}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'restore', version_id: id }),
      });
      await loadDraft();
      setHasDraft(true);
    },
    [page, loadDraft]
  );

  const exit = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete('edit');
    window.location.href = url.toString();
  }, []);

  const value = useCallback(
    (id: string, def?: string) => {
      if (editing) return draft?.[id] ?? published[id] ?? def ?? '';
      return published[id] ?? def ?? '';
    },
    [editing, draft, published]
  );

  const contentCtx: SiteContentCtx = { value, editing, rich: richSet, onEdit };
  const barCtx: BarCtx = {
    editing,
    status,
    hasDraft,
    publishing,
    canUndo,
    canRedo,
    versions,
    seoFields,
    value,
    onEdit,
    publish,
    discard,
    restore,
    undo,
    redo,
    exit,
  };

  return (
    <Ctx.Provider data-component-id="src/components/site-content/SiteContent.tsx:281:4" data-component-path="src/components/site-content/SiteContent.tsx" data-component-line="281" data-component-end-line="283" data-component-file="SiteContent.tsx" data-component-name="Ctx.Provider" data-component-content="%7B%22elementName%22%3A%22Ctx.Provider%22%2C%22value%22%3A%22%5Bvar%3AcontentCtx%5D%22%7D" value={contentCtx}>
      <Bar.Provider data-component-id="src/components/site-content/SiteContent.tsx:282:6" data-component-path="src/components/site-content/SiteContent.tsx" data-component-line="282" data-component-end-line="282" data-component-file="SiteContent.tsx" data-component-name="Bar.Provider" data-component-content="%7B%22elementName%22%3A%22Bar.Provider%22%2C%22value%22%3A%22%5Bvar%3AbarCtx%5D%22%7D" value={barCtx}>{children}</Bar.Provider>
    </Ctx.Provider>
  );
}
